import { inject, Injectable } from "@angular/core";

import { AccountView } from "../../../models/view/account/account.view";
import { GlossSyncInterface } from "@bitwarden/web-vault/app/services/syncing/basiq/sync-levels/basiq.sync";
import { OriginImportData, PlaidImportData } from "../gloss.sync";
import { GlossSyncService } from "@bitwarden/web-vault/app/services/syncing/gloss.sync.service";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { AppStateStoreService } from "../../store/app-state/app-state.store.service";
import { PlaidApi } from "./plaid.api";
import { PlaidLinkService } from "./plaid-link.service";
import { PLAID_LINK_TOKEN_KEY } from "./plaid.constants";
import { UserStoreService } from "../../store/user/user.store.service";
import { ImportStoreService } from "../../store/import/import.store.service";
import { TransactionStoreService } from "../../store/transaction/transaction.store.service";
import { FREE } from "@bitwarden/web-vault/app/models/types/subscription.type";
import { PluginConfigService } from "../../store/data-plugin/plugin-config.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { Origin, SyncStatusEnum } from "../../../models/types/general-types";
import { isCreditType, isMortgageType } from "../../../shared/utils/helper.account/account-type";
import { pollUntil } from "../../../shared/utils/poll-until";
import { TransactionStatusEnum } from "../../../models/enum/transactionType";
import { CategoryView } from "../../../models/view/category/category.view";
import { TransactionView } from "../../../models/view/transaction/transaction.view";
import { SourceTransactionsView } from "../../../models/view/source-transactions/source-transactions.view";
import { SourceAccountView } from "../../../models/view/source-book/source-account.view";
import { PlaidAccountAction } from "./actions/plaid.action.account";
import { PlaidTransactionAction } from "./actions/plaid.action.transaction";
import type {
  GlossPlaidAccountsResponse,
  GlossPlaidItemSummary,
  GlossPlaidTransactionsSyncResponse,
  PlaidAccount,
  PlaidTransaction,
} from "./plaid.types";

const BACKGROUND_POLL_INTERVAL_MS = 10_000;
const BACKGROUND_POLL_MAX_DURATION_MS = 30 * 60 * 1000; // 30 min

/** Initial readiness poll: ~10s (initial_update_complete). */
const INITIAL_READY_MAX_RETRIES = 5;
const INITIAL_READY_DELAY_MS = 2000;

/** Historical readiness poll: ~2 min (historical_update_complete). */
const HISTORICAL_READY_MAX_RETRIES = 24;
const HISTORICAL_READY_DELAY_MS = 5000;

/**
 * Safety cap on transactions/sync pagination drain to avoid an unbounded loop.
 * Each page is ~500 transactions. Raise this value if product requires a deeper
 * initial history — remaining pages are picked up on the next scheduled sync either way.
 */
const MAX_SYNC_PAGES = 50;

/**
 * Thrown by {@link PlaidSync.deleteVaultTransactionsByIds} when the transaction store is not in a known
 * state — null-initialised before tier 2 runs, or nulled by a concurrent clearStore() (logout /
 * purge-vault) mid-sync. A null store means "unknown", NOT "empty": treating it as empty would skip the
 * main-row delete yet still delete the matching source rows and let the cursor commit, stranding the
 * removed mains for good (Plaid never resends removed ids) and leaving a replaced pending row next to
 * its posted replacement (double-counted spend). Throwing routes through the caller's
 * terminateAccountSyncFailure catch, so the account aborts and retries next sync with the cursor
 * uncommitted — the same safe outcome the previous enrich read's TypeError produced, now intentional and
 * clearly logged. Mirrors Fiskil's SyncWindowUnavailableError (#5384).
 */
export class TransactionStoreUnavailableError extends Error {
  constructor(readonly accountId: string | undefined) {
    super(`Plaid transaction store unavailable for account ${accountId ?? "?"}`);
    this.name = "TransactionStoreUnavailableError";
  }
}

@Injectable({
  providedIn: "root",
})
export class PlaidSync implements GlossSyncInterface {
  private plaidApi = inject(PlaidApi);
  private plaidLinkService = inject(PlaidLinkService);
  private glossSyncService = inject(GlossSyncService);
  private appState = inject(AppStateStoreService);
  private userStore = inject(UserStoreService);
  private importStore = inject(ImportStoreService);
  private transactionStore = inject(TransactionStoreService);
  private pluginConfigService = inject(PluginConfigService);
  private plaidAccountAction = inject(PlaidAccountAction);
  private plaidTransactionAction = inject(PlaidTransactionAction);
  private log = inject(LogService);

  private backgroundPollIntervalId: ReturnType<typeof setInterval> | null = null;
  private backgroundPollStartTime: number | null = null;

  async connect(_isFromGuide: boolean = false): Promise<void> {
    if (!this.ensurePaidTierForPlaid()) {
      return;
    }

    try {
      const { link_token } = await this.plaidApi.createLinkToken({});

      // Persist so the OAuth landing page can resume Link after the bank redirect.
      sessionStorage.setItem(PLAID_LINK_TOKEN_KEY, link_token);

      this.endPlaidProcess();
      const result = await this.plaidLinkService.openLink(link_token);

      sessionStorage.removeItem(PLAID_LINK_TOKEN_KEY);

      if (!result) {
        // Clean exit — user cancelled
        return;
      }

      const { publicToken } = result;
      this.appState.startProcess("plaid-syncing-data", "Saving connection...");
      const { item_id } = await this.plaidApi.exchangeToken(publicToken);

      // Persist the vault plugin config so the card shows as "Installed".
      // A config write failure must not break the sync flow.
      try {
        await this.pluginConfigService.ensurePlaidPluginActivated();
      } catch (configError) {
        this.log.error("Failed to persist Plaid plugin config: " + JSON.stringify(configError));
      }

      this.endPlaidProcess();

      // Trigger import directly from onSuccess — do NOT redirect.
      await this.import({ itemId: item_id });
    } catch (error) {
      sessionStorage.removeItem(PLAID_LINK_TOKEN_KEY);
      this.endPlaidProcess();
      await this.glossSyncService.logError([], error, "Connecting to Plaid", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  /**
   * First import after consent. Mirrors Fiskil's fetchDataAfterConsent:
   * poll readiness, fetch accounts + transactions, save to vault, report complete.
   * If readiness times out, fall back to background polling and toast the user.
   */
  async import(originImportData: OriginImportData): Promise<void> {
    if (!this.ensurePaidTierForPlaid()) {
      this.glossSyncService.initializedRunningSyncState();
      return;
    }

    this.glossSyncService.runningSyncState.set({
      ...this.glossSyncService.runningSyncState(),
      isRunning: true,
      isError: false,
      origin: Origin.plaid,
      message: "",
    });

    // Session recovery (interrupted first import): fetch and save without the readiness poll.
    const importData = (originImportData ?? {}) as PlaidImportData;
    if (importData.mode === "recovery") {
      await this.fetchDataForRecovery(importData.itemId);
      return;
    }

    try {
      this.appState.startProcess("plaid-syncing-data", "Waiting for Plaid data...");
      const ready = await this.pollUntilReady();

      if (ready) {
        await this.doFetchAndReportImport(importData.itemId);
        this.endPlaidProcess();
      } else {
        this.endPlaidProcess();
        this.glossSyncService.confirmationDialog.notify("info", "", "plaidDataReadyWeWillNotify");
        this.startBackgroundPlaidPolling();
      }
    } catch (error) {
      this.endPlaidProcess();
      this.appState.endProcess("importing-data");
      const err = error instanceof Error ? error : new Error(String(error));
      this.glossSyncService.runningSyncState.set({
        ...this.glossSyncService.runningSyncState(),
        isRunning: false,
        isError: true,
        message: "somethingWentWrong",
      });
      await this.glossSyncService.logError([], err, "Importing Plaid data", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  /**
   * Recovery mode: fetch accounts + transactions and save without the readiness poll.
   * Used by login bootstrap / manage-accounts when a first import was interrupted.
   * Mirrors {@link FiskilSync.fetchDataForRecovery}.
   */
  private async fetchDataForRecovery(itemId?: string): Promise<void> {
    try {
      this.appState.startProcess("plaid-syncing-data", "Recovering Plaid import...");
      await this.doFetchAndReportImport(itemId);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.glossSyncService.runningSyncState.set({
        ...this.glossSyncService.runningSyncState(),
        isRunning: false,
        isError: true,
        message: "somethingWentWrong",
      });
      await this.glossSyncService.logError([], err, "Plaid recovery import", null);
    } finally {
      this.endPlaidProcess();
    }
  }

  /**
   * Readiness polling per ARCHITECT-REPORT §4.1:
   * first wait for initial_update_complete (~10s), then historical_update_complete (~2 min).
   * @returns true when historical update is complete, false on timeout.
   */
  private async pollUntilReady(): Promise<boolean> {
    const sleep = (ms: number) => this.delay(ms);

    const initial = await pollUntil({
      poll: () => this.plaidApi.getSyncStatus(),
      isReady: (status) =>
        Boolean(status?.historical_update_complete || status?.initial_update_complete),
      intervalMs: INITIAL_READY_DELAY_MS,
      maxAttempts: INITIAL_READY_MAX_RETRIES,
      sleep,
    });
    if (initial.value?.historical_update_complete) {
      return true;
    }

    // Either initial_update_complete fired early or the initial-readiness budget ran out —
    // either way, move on to polling for historical_update_complete.
    const historical = await pollUntil({
      poll: () => this.plaidApi.getSyncStatus(),
      isReady: (status) => Boolean(status?.historical_update_complete),
      intervalMs: HISTORICAL_READY_DELAY_MS,
      maxAttempts: HISTORICAL_READY_MAX_RETRIES,
      sleep,
    });
    return Boolean(historical.value?.historical_update_complete);
  }

  /** Fetch accounts + transactions, save to vault, then report import complete. */
  private async doFetchAndReportImport(itemId?: string): Promise<void> {
    this.stopBackgroundPlaidPolling();
    const accounts = await this.fetchAndSaveAccounts(itemId);
    if (accounts.length > 0) {
      if (!this.glossSyncService.isSilentBootstrap()) {
        await this.glossSyncService.markAsSyncing(accounts);
      }
      const { hadFailures } = await this.fetchSaveAndAckTransactions(accounts, itemId);
      // ACK accountsSaved only after all transaction cursors are committed and every account
      // succeeded. Sending it before fetchSaveAndAckTransactions, or after a partial account
      // failure, left accounts_synced=true with missing transactions, suppressing the
      // vaultAccountsMissingTransactions recovery guard and relying solely on
      // has_unsaved_synced_data to detect the incomplete import.
      await this.reportImportComplete({ accountsSaved: !hadFailures, itemId });
    } else {
      await this.reportImportComplete({ itemId });
      // No per-account terminateSync will run, so reset isRunning explicitly.
      this.glossSyncService.initializedRunningSyncState();
    }
    this.stopBackgroundPlaidPolling();
  }

  private async reportImportComplete(opts?: {
    cursor?: string | null;
    accountsSaved?: boolean;
    itemId?: string;
    updateModeCompleted?: boolean;
  }): Promise<void> {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await this.plaidApi.reportImportComplete({
          // ?? undefined coerces null → undefined: the API rejects an explicit null cursor.
          cursor: opts?.cursor ?? undefined,
          accountsSaved: opts?.accountsSaved,
          itemId: opts?.itemId,
          updateModeCompleted: opts?.updateModeCompleted,
        });
        return;
      } catch (error) {
        if (attempt === 0) {
          await this.delay(500);
          continue;
        }
        this.log.error(`Plaid reportImportComplete failed: ${error}`);
        throw error;
      }
    }
  }

  /** Background poll every 10s; when ready, run the import. Stops after 30 min. */
  private startBackgroundPlaidPolling(): void {
    if (this.backgroundPollIntervalId != null) {
      return;
    }
    this.backgroundPollStartTime = Date.now();

    this.backgroundPollIntervalId = setInterval(async () => {
      if (
        this.backgroundPollStartTime != null &&
        Date.now() - this.backgroundPollStartTime > BACKGROUND_POLL_MAX_DURATION_MS
      ) {
        this.stopBackgroundPlaidPolling();
        this.glossSyncService.initializedRunningSyncState();
        return;
      }

      try {
        const status = await this.plaidApi.getSyncStatus();
        if (status?.historical_update_complete) {
          this.stopBackgroundPlaidPolling();
          // Polling has already stopped, so a failure here cannot be "retried on
          // next tick". Once Plaid reports completion, a failing import points at
          // gloss-api/auth, not a transient delay — fail fast and surface it,
          // mirroring the foreground import() error path.
          try {
            await this.doFetchAndReportImport();
          } catch (importError) {
            const err = importError instanceof Error ? importError : new Error(String(importError));
            this.glossSyncService.runningSyncState.set({
              ...this.glossSyncService.runningSyncState(),
              isRunning: false,
              isError: true,
              message: "somethingWentWrong",
            });
            await this.glossSyncService.logError([], err, "Background Plaid import", null);
            this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
          }
        }
      } catch {
        // ignore getSyncStatus failures — retried on next tick
      }
    }, BACKGROUND_POLL_INTERVAL_MS);
  }

  private stopBackgroundPlaidPolling(): void {
    if (this.backgroundPollIntervalId != null) {
      clearInterval(this.backgroundPollIntervalId);
      this.backgroundPollIntervalId = null;
    }
    this.backgroundPollStartTime = null;
  }

  /**
   * Fetch Plaid accounts, ensure the institution exists, build + save AccountViews and
   * source-account rows. Cached balances only (locked decision #6 — never call balances/get).
   * itemId scopes the request to a specific item so institution_id is always present in the
   * response — required for the Fetch button's institution-matching gate.
   * @returns accounts re-read from the store so each has its vault id (vid).
   */
  private async fetchAndSaveAccounts(itemId?: string): Promise<AccountView[]> {
    const accountsResponse = await this.plaidApi.getAccounts(itemId);
    const plaidAccounts = accountsResponse?.accounts ?? [];
    if (plaidAccounts.length === 0) {
      return [];
    }

    // Org-owned accounts are excluded from the match candidates: a personal account shared/forked
    // into an org (`ManageAccountsComponent.shareAccountToOrg`) keeps the same provider external
    // id (`basiqAccountId`) on both copies, so an unfiltered store-wide search here could resolve
    // to the org fork instead of the personal original and write this refresh's balance/new
    // transactions onto the org's shared cipher instead.
    // `allAccounts` (unfiltered) is kept separately for `buildPlaidSourceAccountView`'s
    // orphan-reuse check, which asks a different question ("does ANY account claim this source
    // row") — filtering it the same way would make a legitimate org-linked source row look
    // orphaned and eligible for silent reuse by this personal refresh.
    const allAccounts = this.userStore.accounts.accountViews() ?? [];
    const existingAccounts = allAccounts.filter((a) => !a.isOrganizationOwned?.());
    const existingPlaidSourceBooks =
      this.importStore.sourceAccount.sourceBookViews()?.filter((s) => s.origin === Origin.plaid) ??
      [];

    this.glossSyncService.runningSyncState.set({
      ...this.glossSyncService.runningSyncState(),
      accountsCount: plaidAccounts.length,
      failedAccountsCount: 0,
      successfulAccountsCount: 0,
    });

    await this.plaidAccountAction.ensurePlaidInstitutionInVault(accountsResponse);

    const savedAccounts: AccountView[] = [];
    const sourceAccountsToSave: SourceAccountView[] = [];
    // Tracks orphan source rows already claimed in this import run so a later
    // same-labelled account can't reuse one (#4181). Shared across every iteration.
    const claimedOrphanIds = new Set<string>();

    for (const plaidAccount of plaidAccounts) {
      let accountForError: AccountView | null = null;
      try {
        const existingAccount = existingAccounts.find(
          (a) => a.origin === Origin.plaid && a.basiqAccountId === plaidAccount.account_id,
        );
        this.appState.startProcess(
          "importing-data",
          `${existingAccount ? "Updating" : "Creating"} ${plaidAccount.name ?? "Account"}`,
        );

        const cachedBalance = this.plaidRawBalance(plaidAccount);

        let rowAccount: AccountView;
        if (existingAccount) {
          const clone = existingAccount.clone();
          clone.c_balance = this.computeBalance(clone, cachedBalance);
          accountForError = clone;
          rowAccount = clone;
        } else {
          const institution = await this.plaidAccountAction.getInstitutionForAccount(
            accountsResponse.institution_id,
          );
          const accountProduct = this.plaidAccountAction.getAccountProduct(
            plaidAccount,
            institution,
          );
          const accountStoreModel = this.plaidAccountAction.toAccountStoreModel(
            plaidAccount,
            institution,
            accountProduct,
          );
          const accountView = new AccountView(accountStoreModel);
          accountView.c_balance = this.computeBalance(accountView, cachedBalance);
          accountForError = accountView;
          rowAccount = accountView;
        }

        const rowSource = this.plaidAccountAction.buildPlaidSourceAccountView(
          plaidAccount,
          rowAccount,
          existingPlaidSourceBooks,
          existingAccounts,
          claimedOrphanIds,
          allAccounts,
        );

        savedAccounts.push(rowAccount);
        sourceAccountsToSave.push(rowSource);
      } catch (error) {
        await this.terminateAccountSyncFailure(
          accountForError,
          error,
          `Plaid import account (${plaidAccount.account_id})`,
        );
      } finally {
        this.appState.endProcess("importing-data");
      }
    }

    this.appState.startProcess("importing-data", "Saving accounts...");
    try {
      if (savedAccounts.length > 0) {
        await this.userStore.accounts.saveToVault(savedAccounts, true);
      }
      if (sourceAccountsToSave.length > 0) {
        await this.importStore.sourceAccount.saveToVault(sourceAccountsToSave, true);
      }
    } finally {
      this.appState.endProcess("importing-data");
    }

    return savedAccounts.length > 0 ? this.accountsWithVaultIdsFromStore(savedAccounts) : [];
  }

  /** Raw balance from Plaid: depository accounts prefer available over current. */
  private plaidRawBalance(plaidAccount: PlaidAccount): number {
    return plaidAccount.type === "depository"
      ? (plaidAccount.balances?.available ?? plaidAccount.balances?.current ?? 0)
      : (plaidAccount.balances?.current ?? 0);
  }

  /**
   * Plaid reports credit-card and mortgage balances as positive amounts-owed.
   * Negate them so they appear as liabilities in the vault (negative balance).
   * Note: the old formula was `cachedBalance - creditLimit`, which produced
   * incorrect results when creditLimit was absent (balance stayed positive).
   */
  private computeBalance(account: AccountView, cachedBalance: number): number {
    return isCreditType(account.type) || isMortgageType(account.type)
      ? -cachedBalance
      : cachedBalance;
  }

  /**
   * Fetches transaction pages one at a time, applying each page to vault and ACKing
   * the cursor (cursor-as-receipt) before fetching the next. Crashed mid-page: cursor
   * stays at last committed position; dedup absorbs the re-fetched overlap on recovery.
   */
  private async fetchSaveAndAckTransactions(
    accounts: AccountView[],
    itemId?: string,
  ): Promise<{ hadFailures: boolean }> {
    return this.drainTransactionPages({
      accounts,
      itemId,
      resolveBalance: (account) => this.cachedBalanceForAccount(account),
      applyErrorLabel: (account) => `Plaid import transactions (${account.id})`,
      finalizeErrorLabel: (account) => `Plaid finalize sync (${account.id})`,
      capWarning: `Plaid initial import hit ${MAX_SYNC_PAGES}-page cap; remaining transactions will be fetched on next sync.`,
    });
  }

  /**
   * Paginates transactions/sync for the given accounts: apply each page delta, ACK the
   * cursor per page, then finalize CBAT + sync status. Shared by first import and
   * incremental sync.
   */
  private async drainTransactionPages(opts: {
    accounts: AccountView[];
    itemId?: string;
    resolveBalance: (account: AccountView) => number;
    applyErrorLabel: (account: AccountView) => string;
    finalizeErrorLabel: (account: AccountView) => string;
    capWarning: string;
  }): Promise<{ hadFailures: boolean }> {
    const { accounts, itemId, resolveBalance, applyErrorLabel, finalizeErrorLabel, capWarning } =
      opts;
    const failedAccountIds = new Set<string>();

    for (let page = 0; page < MAX_SYNC_PAGES; page++) {
      const response: GlossPlaidTransactionsSyncResponse =
        await this.plaidApi.getTransactions(itemId);

      const pageDelta = {
        added: response.added ?? [],
        modified: response.modified ?? [],
        removed: (response.removed ?? []).map((r) => r.transaction_id),
      };

      for (const account of accounts) {
        if (failedAccountIds.has(account.id)) {
          continue;
        }
        try {
          await this.applyDeltaForAccount(account, pageDelta);
        } catch (error) {
          failedAccountIds.add(account.id);
          await this.terminateAccountSyncFailure(account, error, applyErrorLabel(account));
        } finally {
          this.appState.endProcess("importing-data");
        }
      }

      // If any account failed this page, leave the cursor uncommitted so recovery
      // re-fetches the whole page; dedup absorbs the overlap for the accounts that
      // already succeeded.
      if (failedAccountIds.size > 0) {
        break;
      }

      // Commit cursor only after vault save for this page is complete.
      await this.reportImportComplete({ cursor: response.next_cursor, itemId });

      if (!response.has_more) {
        break;
      }

      if (page === MAX_SYNC_PAGES - 1) {
        this.log.warning(capWarning);
      }
    }

    // Finalize balance + sync status for all accounts that made it through.
    for (const account of accounts) {
      if (failedAccountIds.has(account.id)) {
        continue;
      }
      try {
        // saveCBAT catches its own errors and returns false — treat that as a sync failure so the
        // account isn't marked synced and is retried next sync (#5546). Basiq already checks this.
        const cbatSaved = await this.plaidTransactionAction.saveCBAT(
          account,
          resolveBalance(account),
        );
        if (!cbatSaved) {
          throw new Error("saveCBAT failed");
        }
        await this.glossSyncService.terminateSync([account], SyncStatusEnum.synced);
      } catch (error) {
        failedAccountIds.add(account.id);
        await this.terminateAccountSyncFailure(account, error, finalizeErrorLabel(account));
      }
    }

    return { hadFailures: failedAccountIds.size > 0 };
  }

  /**
   * Applies the accumulated delta for a single account.
   *
   * Order is deliberate so a pending row is never resurrected:
   *  1. compute removals: explicit `removed[]` + pending rows referenced by added `pending_transaction_id`
   *  2. delete those vault rows (and their source rows)
   *  3. upsert added + modified: a new id is created; an existing id is UPDATED IN PLACE (its cipher
   *     and saved link state are kept) so a Plaid edit never discards the row's transfer link.
   */
  private async applyDeltaForAccount(
    account: AccountView,
    delta: { added: PlaidTransaction[]; modified: PlaidTransaction[]; removed: string[] },
  ): Promise<void> {
    this.appState.startProcess("importing-data", `Pulling transactions for ${account.name}`);

    try {
      const upserts = [...delta.added, ...delta.modified].filter(
        (tx) => tx.account_id === account.basiqAccountId,
      );

      // Pending-transaction ids referenced by newly posted transactions in `added[]`.
      const pendingIdsToReplace = delta.added
        .filter((tx) => tx.account_id === account.basiqAccountId && !!tx.pending_transaction_id)
        .map((tx) => tx.pending_transaction_id as string);

      // Case-sensitive exact match for removals (explicit removed[] + pending replacements).
      const idsToRemove = new Set<string>([...delta.removed, ...pendingIdsToReplace]);

      await this.deleteVaultTransactionsByIds(account, idsToRemove);

      if (upserts.length === 0) {
        return;
      }

      const { transactionCategoryMap, categoriesToSave } = this.mapPlaidCategoriesToViews(upserts);
      if (categoriesToSave.length > 0) {
        await this.userStore.categories.saveToVault(categoriesToSave, true);
      }

      const { transactionViews, sourceTransactionViews } =
        this.buildPlaidTransactionsWithSourceRows(account, upserts, transactionCategoryMap);

      // A Plaid edit re-imports the row from Plaid data, which carries no link state (`lTid`/`xLid`)
      // and a fresh `vid`. Rather than delete-and-recreate the row — which would silently unlink a
      // saved transfer/conversion pair (#4504 item 7) and, across a multi-account page + a mid-sync
      // crash, lose a same-item transfer entirely (both legs are edited in one page, so a failure on
      // the second leg strands the first and the retry flattens both to unlinked, #4442) — UPDATE the
      // existing row IN PLACE. The main row's stored cipher (`vid`) and link state (`lTid`/`xLid`)
      // are preserved by `upsertPreservingLinkState`, which reads them from the store INSIDE the
      // collection's mutation lock — serialized against `linkPair`/`unlink` — so a link change a user
      // makes mid-sync is honoured, never silently overwritten (and vice-versa; PR #4783). A
      // genuinely new transaction (no stored row) is created as-is. Because the row is never deleted,
      // both legs of a transfer keep their own copy of the link at all times — a crash mid-page can
      // only leave the display fields stale (healed on the next sync), never the link.
      const upsertIds = new Set(transactionViews.map((v) => v.id));

      // Source rows first. Reuse the existing source row's identity (id/`vid`) so the writer UPDATES
      // it in place and the main keeps pointing at it. NOTE: only the source row matched by the main
      // id is reused; a second source row sharing the same `srId` is left as an orphan cipher — that
      // duplicate-source case is reclaimed by the #5303 sweep, not here.
      const existingSourceByTxId = new Map(
        (this.importStore.sourceTransaction.sourceTransactionViews() ?? [])
          .filter((s) => upsertIds.has(s.sourceId))
          .map((s) => [s.sourceId, s] as const),
      );
      // The source id each main row will point at (existing id when reused, else the fresh one).
      const resolvedSourceIds: string[] = [];
      const sourceRowsToSave = transactionViews.map((view, i) => {
        const source = sourceTransactionViews[i].toStoreModel();
        const existingSource = existingSourceByTxId.get(view.id)?.toStoreModel();
        if (existingSource) {
          source.id = existingSource.id;
          source.vid = existingSource.vid;
          source.dc = existingSource.dc;
        }
        resolvedSourceIds[i] = source.id;
        return new SourceTransactionsView(source);
      });

      if (sourceRowsToSave.length > 0) {
        await this.importStore.sourceTransaction.saveToVault(sourceRowsToSave, true);
      }

      // Main rows: rebuilt from Plaid data and pointed at the resolved source id. The stored cipher
      // and link state are merged in by `upsertPreservingLinkState` under the mutation lock (above),
      // so nothing here snapshots link state ahead of the write.
      const transactionRowsToSave = transactionViews.map((view, i) => {
        const main = view.toStoreModel();
        main.srId = resolvedSourceIds[i];
        return new TransactionView(main);
      });

      await this.transactionStore.transaction.upsertPreservingLinkState(transactionRowsToSave);
    } finally {
      this.appState.endProcess("importing-data");
    }
  }

  /**
   * Deletes vault transactions (and their source rows) whose id matches exactly (case-sensitive).
   *
   * Used for genuine removals only (`removed[]` + pending replacements): the repairing
   * `deleteFromVault` strips the removed id from any surviving partner's `lTid`, so no one is left
   * pointing at a deleted transaction. The upsert-replace path does NOT delete — it updates rows in
   * place (see `applyDeltaForAccount`).
   */
  private async deleteVaultTransactionsByIds(
    account: AccountView,
    ids: Set<string>,
  ): Promise<void> {
    if (ids.size === 0) {
      return;
    }
    // Read the transaction store, not the enriched collection: enrich stays null until generate()
    // runs (deferred for the whole sync while refresh is blocked) and clearStore() resets it, so an
    // enrich read here throws on removals; and even when non-null it lags rows upserted earlier in
    // this same drain run, so a pending main replaced by a posted row on a later page isn't found and
    // survives alongside its replacement. Mirrors #5541 / #5532.
    //
    // A null store is "unknown", not "empty": coalescing to [] here would skip the main delete yet
    // still delete the matching source rows below and let the cursor commit — see
    // TransactionStoreUnavailableError. Throw instead so the account aborts and retries (#5384).
    const storeTransactions = this.transactionStore.transaction.transactionViews();
    if (storeTransactions == null) {
      throw new TransactionStoreUnavailableError(account.id);
    }
    const accountTransactions = storeTransactions.filter(
      (t) =>
        t.accountLink?.id === account.id &&
        t.definition === TransactionStatusEnum.transaction &&
        ids.has(t.id),
    );

    // Match source rows by id but scoped to THIS account. `ids` (from `delta.removed`) is item-wide
    // — the same list is applied to every account — so an unscoped `ids.has(s.sourceId)` would let
    // one account's pass delete another account's source rows and invert the main-before-source
    // ordering across accounts. Scoping by `accountId` keeps it per-account while still not gating
    // source cleanup on the main rows still existing: if a prior sync deleted the mains but its
    // source cleanup then failed, the retry finds no mains yet must still delete the stranded
    // source rows (#4504 item 8).
    const sourceTransactions = this.importStore.sourceTransaction
      .sourceTransactionViews()
      .filter((s) => s.accountId === account.id && ids.has(s.sourceId));

    if (accountTransactions.length === 0 && sourceTransactions.length === 0) {
      return;
    }

    // Delete the main transactions first and let any failure propagate — including a
    // PartialTransactionDeleteError (a surviving partner's repair committed but the delete write
    // failed). Aborting the account keeps the sync cursor uncommitted, so the whole page (including
    // removed[]) is re-fetched and the delete retried on the next sync. Swallowing here instead
    // would let the cursor advance past removed[] (those ids never come back). Deleting the source
    // rows only after the main delete succeeds avoids stranding rows on a partial failure. (Plaid
    // deletes mains-first — unlike Basiq's sources-first — because its source match reads
    // `ids.has(s.sourceId)` from `delta.removed`, not the main rows, so sources are still found after
    // the mains are gone, and the cursor stays uncommitted on failure so the account retries.)
    if (accountTransactions.length > 0) {
      await this.transactionStore.transaction.deleteFromVault(accountTransactions);
    }

    if (sourceTransactions.length > 0) {
      await this.importStore.sourceTransaction.deleteFromVault(sourceTransactions);
    }
  }

  /** Builds main + source transaction rows, linking `transaction.sourceLink.id` to the source row. */
  private buildPlaidTransactionsWithSourceRows(
    account: AccountView,
    plaidTransactions: PlaidTransaction[],
    transactionCategoryMap: Map<string, CategoryView[]>,
  ): { transactionViews: TransactionView[]; sourceTransactionViews: SourceTransactionsView[] } {
    const transactionViews: TransactionView[] = [];
    const sourceTransactionViews: SourceTransactionsView[] = [];

    for (const tx of plaidTransactions) {
      const categoryViews = transactionCategoryMap.get(tx.transaction_id);
      const transactionView = this.plaidTransactionAction.toTransactionView(
        tx,
        account,
        categoryViews,
      );
      const categoryNames = (categoryViews ?? []).map((c) => c.name);
      const sourceView = this.plaidTransactionAction.toSourceTransactionView(
        account,
        transactionView,
        categoryNames,
        tx,
      );
      transactionView.sourceLink.id = sourceView.id;
      transactionViews.push(transactionView);
      sourceTransactionViews.push(sourceView);
    }

    return { transactionViews, sourceTransactionViews };
  }

  /**
   * Maps Plaid personal_finance_category to CategoryViews (sc: "plaid").
   * Resolves existing categories from vault, creates new ones for the batch, deduplicates by name.
   * Does NOT use the deprecated `category[]` array.
   */
  private mapPlaidCategoriesToViews(plaidTransactions: PlaidTransaction[]): {
    transactionCategoryMap: Map<string, CategoryView[]>;
    categoriesToSave: CategoryView[];
  } {
    const transactionCategoryMap = new Map<string, CategoryView[]>();
    const categoryByName = new Map<string, CategoryView>();
    const categoriesToSave: CategoryView[] = [];
    const existingCategories = this.userStore.categories.categoryViews();

    const getOrCreateCategory = (name: string): CategoryView => {
      const key = name.toLowerCase();
      const existing = categoryByName.get(key);
      if (existing) {
        return existing;
      }
      const inVault = existingCategories.find((c) => c.name.toLowerCase() === key);
      if (inVault) {
        categoryByName.set(key, inVault);
        return inVault;
      }
      const newCategory = new CategoryView({
        dc: new Date().toISOString(),
        dm: new Date().toISOString(),
        gd: false,
        sc: "plaid",
        id: crypto.randomUUID(),
        n: name,
        v: 2,
        vid: null,
      });
      categoryByName.set(key, newCategory);
      categoriesToSave.push(newCategory);
      return newCategory;
    };

    const getCategoryName = (tx: PlaidTransaction): string | undefined => {
      const cat = tx.personal_finance_category;
      if (!cat) {
        return undefined;
      }
      return cat.detailed ?? cat.primary;
    };

    for (const tx of plaidTransactions) {
      const name = getCategoryName(tx);
      const categories: CategoryView[] = name ? [getOrCreateCategory(name)] : [];
      transactionCategoryMap.set(tx.transaction_id, categories);
    }

    return { transactionCategoryMap, categoriesToSave };
  }

  /**
   * Cached current balance for an account; uses the account's stored c_balance.
   * Plaid never calls balances/get (locked decision #6) so we reuse what we already have.
   */
  private cachedBalanceForAccount(account: AccountView): number {
    return account.c_balance ?? 0;
  }

  /**
   * Return accounts from the vault store so each view has vaultId (`vid`) for subsequent saves.
   * Mirrors Fiskil's accountsWithVaultIdsFromStore.
   */
  private accountsWithVaultIdsFromStore(accounts: AccountView[]): AccountView[] {
    const fromStore = this.userStore.accounts.accountViews() ?? [];
    return fromStore.reduce<AccountView[]>((acc, view) => {
      const match = accounts.find((account) => account.id === view.id);
      if (match) {
        const clone = view.clone();
        clone.c_balance = match.c_balance;
        acc.push(clone);
      }
      return acc;
    }, []);
  }

  private async terminateAccountSyncFailure(
    account: AccountView | null,
    error: unknown,
    level: string,
  ): Promise<void> {
    const err = error instanceof Error ? error : new Error(String(error));
    this.glossSyncService.runningSyncState.set({
      ...this.glossSyncService.runningSyncState(),
      isRunning: false,
      isError: true,
      message: "somethingWentWrong",
    });
    await this.glossSyncService.logError(
      account ? [account] : [],
      err,
      level,
      SyncStatusEnum.unknownError,
    );
    if (account) {
      await this.glossSyncService.terminateSync([account], SyncStatusEnum.unknownError, err);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Incremental sync (no user re-auth). Checks Item health first, then per healthy Item refreshes
   * cached balances + delta-merges transactions for the vault accounts it covers.
   */
  async sync(accounts: AccountView[]): Promise<void> {
    if (accounts.length === 0) {
      return;
    }
    if (!this.ensurePaidTierForPlaid()) {
      await this.glossSyncService.terminateSync(accounts, SyncStatusEnum.unknownError);
      return;
    }
    try {
      await this.syncAccounts(accounts);
    } catch (error) {
      this.appState.endProcess("importing-data");
      await this.glossSyncService.logError(accounts, error, "Syncing Plaid accounts", null);
      throw error;
    }
  }

  /**
   * Item health gates the sync (ARCHITECT-REPORT §8.6): accounts behind an Item that requires
   * re-auth — or that is no longer linked — are skipped with an error state (re-auth UI is CLIENT-C).
   * For each healthy Item we refresh balances (`/accounts/get`, cached only — decision #6), drain
   * the `transactions/sync` delta, merge it, and write a CBAT. No user re-consent is needed.
   */
  private async syncAccounts(accounts: AccountView[]): Promise<void> {
    let items: GlossPlaidItemSummary[];
    try {
      const itemsResponse = await this.plaidApi.getItems();
      items = itemsResponse?.items ?? [];
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      for (const account of accounts) {
        await this.terminateAccountSyncFailure(account, err, "Plaid getItems for sync");
      }
      return;
    }

    const healthyItemIds = items
      .filter((item) => !item.requires_reauth)
      .map((item) => item.item_id);

    const coveredAccountIds = new Set<string>();
    for (const itemId of healthyItemIds) {
      try {
        await this.syncItemAccounts(itemId, accounts, coveredAccountIds);
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        await this.glossSyncService.logError([], err, `Plaid sync item (${itemId})`, null);
      }
    }

    // Accounts behind a re-auth-required / unlinked Item (or one that failed to refresh) cannot
    // sync without user action — flag them so the UI can prompt reconnect (CLIENT-C).
    for (const account of accounts) {
      if (!coveredAccountIds.has(account.id)) {
        await this.glossSyncService.terminateSync([account], SyncStatusEnum.noConnection);
      }
    }
  }

  /**
   * Refresh + delta-merge the vault accounts covered by a single healthy Item. Maps accounts to the
   * Item via `/accounts/get` (account_id ↔ basiqAccountId) since accounts don't store item_id, and
   * records covered vault ids so the caller can flag the remainder.
   * Applies and ACKs one page at a time (cursor-as-receipt) so the cursor never advances
   * past data that hasn't been confirmed saved to vault.
   */
  private async syncItemAccounts(
    itemId: string,
    accounts: AccountView[],
    coveredAccountIds: Set<string>,
  ): Promise<void> {
    let plaidAccounts: PlaidAccount[];
    try {
      const accountsResponse = await this.plaidApi.getAccounts(itemId);
      plaidAccounts = accountsResponse?.accounts ?? [];
    } catch (error) {
      // This Item failed mid-sync: leave its accounts uncovered (flagged by the caller); don't
      // disturb accounts belonging to other healthy Items.
      const err = error instanceof Error ? error : new Error(String(error));
      await this.glossSyncService.logError([], err, `Plaid getAccounts for sync (${itemId})`, null);
      return;
    }

    const plaidAccountIds = new Set(plaidAccounts.map((a) => a.account_id));
    const itemAccounts = accounts.filter((a) => plaidAccountIds.has(a.basiqAccountId));
    if (itemAccounts.length === 0) {
      return;
    }

    // Mark all item accounts covered before pagination so the caller doesn't flag them
    // as noConnection if a later page fails.
    for (const account of itemAccounts) {
      coveredAccountIds.add(account.id);
    }

    try {
      await this.drainTransactionPages({
        accounts: itemAccounts,
        itemId,
        resolveBalance: (account) => this.refreshedBalanceForAccount(account, plaidAccounts),
        applyErrorLabel: (account) => `Syncing Plaid account (${account.id})`,
        finalizeErrorLabel: (account) => `Syncing Plaid account (${account.id})`,
        capWarning: `Plaid sync hit ${MAX_SYNC_PAGES}-page cap for item ${itemId}; remaining transactions will be fetched on next sync.`,
      });
    } catch (error) {
      // This Item failed mid-sync: flag its accounts and continue with other healthy Items.
      const err = error instanceof Error ? error : new Error(String(error));
      await this.glossSyncService.logError([], err, `Plaid sync item (${itemId})`, null);
      for (const account of itemAccounts) {
        await this.terminateAccountSyncFailure(
          account,
          err,
          `Syncing Plaid account (${account.id})`,
        );
      }
    }
  }

  /** Fresh cached balance for a vault account from `/accounts/get`, netted for credit accounts. */
  private refreshedBalanceForAccount(account: AccountView, plaidAccounts: PlaidAccount[]): number {
    const match = plaidAccounts.find((a) => a.account_id === account.basiqAccountId);
    const cachedBalance = match ? this.plaidRawBalance(match) : (account.c_balance ?? 0);
    return this.computeBalance(account, cachedBalance);
  }

  async connectToInstitution(_institution: InstitutionView): Promise<void> {
    await this.connect();
  }

  async reconnectToInstitution(institution: InstitutionView): Promise<void> {
    if (!this.ensurePaidTierForPlaid()) {
      return;
    }
    const itemId = await this.resolveItemIdForInstitution(
      institution,
      "Plaid getItems for reconnect",
    );
    if (!itemId) {
      return;
    }
    await this.openUpdateModeLink(itemId);
  }

  async addAccounts(institution: InstitutionView): Promise<void> {
    if (!this.ensurePaidTierForPlaid()) {
      return;
    }
    const itemId = await this.resolveItemIdForInstitution(
      institution,
      "Plaid getItems for add-accounts",
    );
    if (!itemId) {
      return;
    }
    await this.openUpdateModeLink(itemId, true);
  }

  private async resolveItemIdForInstitution(
    institution: InstitutionView,
    logLabel: string,
  ): Promise<string | null> {
    try {
      const { items } = await this.plaidApi.getItems();
      const item = items.find(
        (i) => i.institution_id != null && i.institution_id === institution.instoPlaidId,
      );
      if (!item?.item_id) {
        this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
        return null;
      }
      return item.item_id;
    } catch (error) {
      await this.glossSyncService.logError([], error, logLabel, null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
      return null;
    }
  }

  private async openUpdateModeLink(
    itemId: string,
    accountSelectionOnly: boolean = false,
  ): Promise<void> {
    try {
      const tokenPayload: Parameters<typeof this.plaidApi.createLinkToken>[0] = {
        item_id: itemId,
      };
      if (accountSelectionOnly) {
        tokenPayload.account_selection_only = true;
      }

      const { link_token } = await this.plaidApi.createLinkToken(tokenPayload);

      sessionStorage.setItem(PLAID_LINK_TOKEN_KEY, link_token);

      this.endPlaidProcess();
      const result = await this.plaidLinkService.openLink(link_token);

      sessionStorage.removeItem(PLAID_LINK_TOKEN_KEY);

      if (!result) {
        return;
      }

      // Update Mode: no token exchange. Re-import to pick up re-authed access or new accounts.
      await this.fetchDataForRecovery(itemId);
      // Signal the backend to clear new_accounts_available now that the user
      // has completed Update Mode. Must come after fetchDataForRecovery so the
      // data is already saved before the flag is cleared.
      await this.reportImportComplete({ updateModeCompleted: true, itemId });
    } catch (error) {
      sessionStorage.removeItem(PLAID_LINK_TOKEN_KEY);
      this.endPlaidProcess();
      await this.glossSyncService.logError([], error, "Plaid Update Mode", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  /**
   * Plaid requires a non-free subscription tier.
   * Mirrors {@link FiskilSync.isNoFreeSubscriptionTier}; missing subscription is not eligible.
   */
  private isNoFreeSubscriptionTier(): boolean {
    const sub = this.userStore.subscription.activeSubscription();
    return sub != null && sub.tier !== FREE;
  }

  private endPlaidProcess(): void {
    if (
      !this.glossSyncService.isSilentBootstrap() &&
      this.appState.activeProcesses.has("plaid-syncing-data")
    ) {
      this.appState.endProcess("plaid-syncing-data");
    }
  }

  /** @returns true if Plaid sync can proceed; otherwise shows a toast and returns false */
  private ensurePaidTierForPlaid(): boolean {
    if (this.isNoFreeSubscriptionTier()) {
      return true;
    }
    // Status still resolving: don't block with a "Bundle required" dialog while
    // activeSubscription() is unresolved — it flashes for paid users on load (#14137).
    if (!this.userStore.subscription.isResolved()) {
      return false;
    }
    this.glossSyncService.confirmationDialog.notify(
      "warning",
      "premiumRequired",
      "premiumRequiredDesc",
    );
    return false;
  }
}
